import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Icon, ButtonGroup as QomponentsButtonGroup } from '@seeqdev/qomponents';
import type { ComponentProps, ReactNode } from 'react';

export interface ButtonItem<T extends string = string> {
  label: string;
  id: string;
  value: T;
  disabled?: boolean;
  tooltip?: string;
  icon?: ComponentProps<typeof Icon>['icon'];
}

/**
 * An input component with a group of buttons corresponding to selectable options
 */
export interface ButtonGroupProps<T extends string = string> {
  buttonItems: (ButtonItem<T> & { isActive?: boolean })[];
  name: string;
  value: T;
  onChange: (value: T) => void;
  testId?: string;
}

export const ButtonGroup = <T extends string = string>({
  buttonItems,

  value,
  onChange,
  testId,
}: ButtonGroupProps<T>): ReactNode => {
  const { t } = useTranslation();
  const selected = useMemo(() => value || buttonItems[0]?.value, [value, buttonItems]);

  const buttons = useMemo(
    () =>
      buttonItems.map((buttonItem) => ({
        variant: 'button' as const,
        buttonProps: {
          ...(buttonItem.id && { id: buttonItem.id }),
          ...(buttonItem.label && { label: t(buttonItem.label) }),
          ...(buttonItem.icon && { icon: buttonItem.icon }),
          value: buttonItem.value,
          isActive: buttonItem?.isActive ?? buttonItem.value === selected,
          ...(buttonItem.disabled !== undefined && { disabled: buttonItem.disabled }),
          ...(buttonItem.tooltip && { tooltip: buttonItem.tooltip }),
          variant: 'outline' as const,
          size: 'xs' as const,
          extraClassNames: 'tw:min-w-sq-70!',
        },
      })),
    [buttonItems, selected, t],
  );

  return (
    <QomponentsButtonGroup
      buttons={buttons}
      onChange={(newValue) => onChange(newValue as T)}
      testId={testId}
      extraClassNames="tw:mt-sq-5"
    />
  );
};
